import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

export interface TriSourceSummary {
  decision: string;
  decision_label: string;
  solar_pressure_level: string;
  confidence: string;
  headline: string;
}

export interface WeatherIndexSource {
  model: string;
  solar_midday_index: number | null;
  wind_daily_index: number | null;
  solar_level: string;
  wind_level: string;
  confidence: string;
}

export interface OfficialForecastSource {
  solar_midday_index: number | null;
  wind_daily_index: number | null;
  solar_midday_mw: number;
  wind_daily_mw: number;
  solar_level: string;
  wind_level: string;
}

export interface BulletinSource {
  available: boolean;
  date: string | null;
  has_risk: boolean;
  reasons: string[];
  key_regions_affected: string[];
}

export interface RealizationDetail {
  date: string;
  forecast_midday_mw: number;
  actual_midday_mw: number;
  forecast_midday_index: number;
  realization_ratio: number | null;
  watch_triggered: boolean;
}

export interface RecentRealizationSource {
  curtailment_watch: boolean;
  watch_days: number;
  days_analyzed: number;
  details: RealizationDetail[];
}

export interface TriSourceSignalResponse {
  date: string | null;
  summary: TriSourceSummary | null;
  sources: {
    weather_index: WeatherIndexSource;
    official_forecast: OfficialForecastSource;
    weather_bulletin: BulletinSource;
    recent_realization: RecentRealizationSource;
  };
  conflicts: string[];
  trading_notes: string[];
  data_freshness: string;
  message: string | null;
}

export function useTriSourceSignal() {
  return useQuery({
    queryKey: ['weather', 'tri-source-signal'],
    queryFn: async () => {
      const res = await apiClient.get<TriSourceSignalResponse>('/weather/tri-source-signal');
      return res.data;
    },
    staleTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}
